import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Bell, MessageCircle, LogOut, User, Clock, Building2, Shield, ChevronDown, HelpCircle, BookOpen, LifeBuoy, FilePlus, ClipboardList, Settings, Calendar, Layers, Coins, DollarSign, Link2, Mail, CheckCheck, FileText, Receipt, Banknote, X, Car, Sun, Moon } from 'lucide-react';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/client';
import GlobalSearch from './GlobalSearch';
import VoiceAgentButton from './VoiceAgentButton';

const SETTINGS_ITEMS = [
  { path: '/financial-years',      label: 'Financial Years',      permission: 'manage_finance_settings', icon: Calendar },
  { path: '/gl-accounts',          label: 'GL Accounts',          permission: 'manage_finance_settings', icon: Layers },
  { path: '/currencies',           label: 'Currencies',           permission: 'manage_finance_settings', icon: Coins },
  { path: '/exchange-rates',       label: 'Exchange Rates',       permission: 'manage_finance_settings', icon: DollarSign },
  { path: '/mileage-rates',        label: 'Mileage Rates',        permission: 'manage_finance_settings', icon: Car },
  { path: '/finance-integrations', label: 'Finance Integrations', permission: 'manage_company_settings', icon: Link2 },
  { path: '/approval-slas',        label: 'Approval SLAs',        permission: 'manage_finance_settings', icon: Clock },
  { path: '/company-settings',     label: 'Company Settings',     permission: 'manage_company_settings', icon: Settings },
  { path: '/email-test',           label: 'Email Test',           permission: null, superAdminOnly: true, icon: Mail },
];

// Map notification type to a human-readable label + icon + navigation path
function notifMeta(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('invoice'))  return { label: 'Invoice',  icon: FileText,  path: '/invoices' };
  if (t.includes('expense'))  return { label: 'Expense',  icon: Receipt,   path: '/expenses' };
  if (t.includes('payment'))  return { label: 'Payment',  icon: Banknote,  path: '/invoices' };
  if (t.includes('approval')) return { label: 'Approval', icon: CheckCheck, path: '/invoices' };
  return { label: type || 'Notification', icon: Bell, path: null };
}

// Derive navigation path from entity type + id, falling back to type-based path
function getNotifPath(n) {
  const entity = (n.EntityType || '').toLowerCase();
  const id = n.EntityID;
  if (entity === 'invoice' && id) return `/invoices/${id}`;
  if (entity === 'expense' && id) return `/expenses/${id}`;
  return notifMeta(n.NotificationType).path;
}

export default function TopBar({ onOpenChat, pendingCounts }) {
  const { socket } = useSocket();
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [unreadChat, setUnreadChat] = useState(0);
  const [showNotifDrop, setShowNotifDrop] = useState(false);
  const [showUserDrop, setShowUserDrop] = useState(false);
  const [showHelpDrop, setShowHelpDrop] = useState(false);
  const [showSettingsDrop, setShowSettingsDrop] = useState(false);
  const [notifTab, setNotifTab] = useState('tasks'); // 'tasks' | 'inbox'
  const [inbox, setInbox] = useState([]);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [sessionSeconds, setSessionSeconds] = useState(0);

  const notifRef    = useRef(null);
  const userRef     = useRef(null);
  const helpRef     = useRef(null);
  const settingsRef = useRef(null);

  // Per-task visibility based on role/permissions
  // Note: invoiceCount/expenseCount from the API are already user-scoped (only items
  // where the current user is the current approver), so they are always relevant.
  const canSeeFinanceTasks      = hasPermission('manage_finance_settings');
  const canSeeCompanyInvoices   = canSeeFinanceTasks || hasPermission('manage_invoices');
  const canSeeCompanyExpenses   = canSeeFinanceTasks || hasPermission('manage_expenses');
  const canSeeVendorApprovals   = hasPermission('manage_vendors') || canSeeFinanceTasks;
  const canSeePurchaseOrders    = canSeeFinanceTasks || hasPermission('manage_invoices');
  const canSeeAccruals          = canSeeFinanceTasks;

  // Task count — only counts items the current user is permitted to act on
  const taskCount = useMemo(() => {
    if (!pendingCounts) return 0;
    return (pendingCounts.invoiceCount || 0)
      +    (pendingCounts.expenseCount || 0)
      +    (canSeeCompanyInvoices  ? (pendingCounts.companyInvoiceCount || 0)    : 0)
      +    (canSeeCompanyInvoices  ? (pendingCounts.draftInvoiceCount || 0)      : 0)
      +    (canSeeCompanyExpenses  ? (pendingCounts.companyExpenseCount || 0)    : 0)
      +    (canSeeFinanceTasks     ? (pendingCounts.rateReconciliationCount || 0): 0)
      +    (canSeeVendorApprovals  ? (pendingCounts.vendorApprovalCount || 0)    : 0)
      +    (canSeeFinanceTasks     ? (pendingCounts.postToFinanceCount || 0)     : 0)
      +    (canSeeAccruals         ? (pendingCounts.unmatchedAccrualCount || 0)  : 0)
      +    (canSeePurchaseOrders   ? (pendingCounts.unmatchedPOCount || 0)       : 0);
  }, [pendingCounts, canSeeCompanyInvoices, canSeeCompanyExpenses, canSeeFinanceTasks, canSeeVendorApprovals, canSeeAccruals, canSeePurchaseOrders]);

  // Total badge = tasks + inbox unread
  const totalBadge = taskCount + inboxUnread;

  // Load inbox on mount and every 60s
  useEffect(() => {
    loadInbox();
    const iv = setInterval(loadInbox, 60000);
    return () => clearInterval(iv);
  }, []);

  // Fetch unread chat count
  useEffect(() => {
    loadUnreadChat();
    const iv = setInterval(loadUnreadChat, 30000);
    return () => clearInterval(iv);
  }, []);

  // Listen for new chat messages to bump unread
  useEffect(() => {
    if (!socket) return;
    const handler = () => loadUnreadChat();
    socket.on('chat:message', handler);
    return () => socket.off('chat:message', handler);
  }, [socket]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifDrop(false);
      if (userRef.current && !userRef.current.contains(e.target)) setShowUserDrop(false);
      if (helpRef.current && !helpRef.current.contains(e.target)) setShowHelpDrop(false);
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettingsDrop(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Session timer
  useEffect(() => {
    if (!sessionStorage.getItem('loginTimestamp')) {
      sessionStorage.setItem('loginTimestamp', Date.now().toString());
    }
    const tick = () => {
      const start = parseInt(sessionStorage.getItem('loginTimestamp') || Date.now(), 10);
      setSessionSeconds(Math.floor((Date.now() - start) / 1000));
    };
    tick();
    const iv = setInterval(tick, 60000);
    return () => clearInterval(iv);
  }, []);

  async function loadUnreadChat() {
    try {
      const res = await apiClient.get('/chat/unread-count');
      setUnreadChat(res.data.unreadCount || 0);
    } catch { /* ignore */ }
  }

  async function loadInbox() {
    try {
      const res = await apiClient.get('/notifications/inbox');
      setInbox(res.data.notifications || []);
      setInboxUnread(res.data.unreadCount || 0);
    } catch { /* ignore */ }
  }

  function toggleNotifDropdown() {
    const opening = !showNotifDrop;
    setShowNotifDrop(opening);
    setShowUserDrop(false);
    setShowHelpDrop(false);
    setShowSettingsDrop(false);
    if (opening) loadInbox();
  }

  async function markAllRead() {
    try {
      await apiClient.put('/notifications/inbox/mark-all-read');
      setInbox([]);
      setInboxUnread(0);
    } catch { /* ignore */ }
  }

  async function dismissNotification(notificationId) {
    // Optimistically remove from list
    setInbox(prev => {
      const updated = prev.filter(n => n.NotificationID !== notificationId);
      setInboxUnread(updated.filter(n => !n.IsRead).length);
      return updated;
    });
    // Mark as read on server (fire and forget)
    apiClient.put(`/notifications/inbox/${notificationId}/read`).catch(() => {});
  }

  async function handleNotifClick(n) {
    // Open the detail modal instead of immediately navigating
    setSelectedNotif(n);
  }

  function handleNotifAcknowledge() {
    if (!selectedNotif) return;
    const id = selectedNotif.NotificationID;
    setSelectedNotif(null);
    dismissNotification(id);
  }

  function handleNotifView() {
    if (!selectedNotif) return;
    const path = getNotifPath(selectedNotif);
    const id = selectedNotif.NotificationID;
    setSelectedNotif(null);
    setShowNotifDrop(false);
    dismissNotification(id);
    if (path) navigate(path);
  }

  function toggleUserDropdown() {
    setShowUserDrop(prev => !prev);
    setShowNotifDrop(false);
  }

  async function handleLogout() {
    sessionStorage.removeItem('loginTimestamp');
    await logout();
    navigate('/login');
  }

  const firstName = user?.firstName || user?.FirstName || '';
  const lastName  = user?.lastName  || user?.LastName  || '';
  const email     = user?.email     || user?.Email     || '';
  const username  = user?.username  || user?.Username  || '';

  const initials = useMemo(() => {
    if (firstName && lastName) return (firstName[0] + lastName[0]).toUpperCase();
    if (email) return email[0].toUpperCase();
    if (username) return username[0].toUpperCase();
    return 'U';
  }, [firstName, lastName, email, username]);

  const displayName = useMemo(() => {
    if (firstName && lastName) return `${firstName} ${lastName}`;
    return username || email || 'User';
  }, [firstName, lastName, username, email]);

  const roleBadge = user?.isSuperAdmin
    ? 'Platform Owner'
    : (user?.roleNames?.length > 0 ? user.roleNames.join(', ') : (user?.role || user?.RoleName || 'User'));

  const companyName = user?.defaultCompany?.companyName
    || user?.defaultCompany?.CompanyName
    || 'No Company';

  function formatDuration(totalSec) {
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  }

  function formatNotifDate(dateStr) {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }

  return (
    <div className="hidden lg:flex items-center gap-3 h-12 px-6 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      {/* Global search */}
      <GlobalSearch />

      {/* Spacer */}
      <div className="flex-1" />

      {/* Company name chip */}
      {companyName && companyName !== 'No Company' && (
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-700/60 rounded-lg border border-gray-200 dark:border-gray-600/50 hidden xl:flex">
          <Building2 className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
          <span className="text-xs font-medium text-gray-600 dark:text-gray-200 truncate max-w-[200px]">{companyName}</span>
        </div>
      )}

      {/* ── Notification bell ── */}
      <div className="relative" ref={notifRef}>
        <button
          onClick={toggleNotifDropdown}
          className="relative p-2 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title="Notifications"
        >
          <Bell className="h-5 w-5" />
          {totalBadge > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {totalBadge > 9 ? '9+' : totalBadge}
            </span>
          )}
        </button>

        {/* ── Notification dropdown ── */}
        {showNotifDrop && (
          <div className="absolute right-0 top-12 w-88 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden" style={{ width: 360 }}>
            {/* Header with tabs */}
            <div className="flex items-center border-b border-gray-200">
              <button
                onClick={() => setNotifTab('tasks')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${notifTab === 'tasks' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Tasks
                {taskCount > 0 && (
                  <span className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
                    {taskCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setNotifTab('inbox')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${notifTab === 'inbox' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Inbox
                {inboxUnread > 0 && (
                  <span className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {inboxUnread}
                  </span>
                )}
              </button>
            </div>

            {/* Tasks tab */}
            {notifTab === 'tasks' && (
              <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                {taskCount === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-400">All caught up!</div>
                ) : (
                  <>
                    {(pendingCounts?.invoiceCount || 0) > 0 && (
                      <Link to="/invoices" onClick={() => setShowNotifDrop(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-3.5 h-3.5 text-blue-600" />
                          </div>
                          <span>Invoice Approvals (mine)</span>
                        </div>
                        <span className="font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full text-xs">{pendingCounts.invoiceCount}</span>
                      </Link>
                    )}
                    {(pendingCounts?.expenseCount || 0) > 0 && (
                      <Link to="/expenses" onClick={() => setShowNotifDrop(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <Receipt className="w-3.5 h-3.5 text-green-600" />
                          </div>
                          <span>Expense Approvals (mine)</span>
                        </div>
                        <span className="font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full text-xs">{pendingCounts.expenseCount}</span>
                      </Link>
                    )}
                    {canSeeCompanyInvoices && (pendingCounts?.companyInvoiceCount || 0) > 0 && (
                      <Link to="/invoices?status=Pending" onClick={() => setShowNotifDrop(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-3.5 h-3.5 text-indigo-600" />
                          </div>
                          <span>Invoices to Process</span>
                        </div>
                        <span className="font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full text-xs">{pendingCounts.companyInvoiceCount}</span>
                      </Link>
                    )}
                    {canSeeCompanyInvoices && (pendingCounts?.draftInvoiceCount || 0) > 0 && (
                      <Link to="/invoices?status=Draft" onClick={() => setShowNotifDrop(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <FilePlus className="w-3.5 h-3.5 text-gray-600" />
                          </div>
                          <span>Draft Invoices</span>
                        </div>
                        <span className="font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full text-xs">{pendingCounts.draftInvoiceCount}</span>
                      </Link>
                    )}
                    {canSeeCompanyExpenses && (pendingCounts?.companyExpenseCount || 0) > 0 && (
                      <Link to="/expenses?status=Pending" onClick={() => setShowNotifDrop(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                            <Receipt className="w-3.5 h-3.5 text-teal-600" />
                          </div>
                          <span>Expenses to Process</span>
                        </div>
                        <span className="font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full text-xs">{pendingCounts.companyExpenseCount}</span>
                      </Link>
                    )}
                    {canSeeFinanceTasks && (pendingCounts?.rateReconciliationCount || 0) > 0 && (
                      <Link to="/exchange-rate-reconciliation" onClick={() => setShowNotifDrop(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <Banknote className="w-3.5 h-3.5 text-purple-600" />
                          </div>
                          <span>Rate Reconciliation</span>
                        </div>
                        <span className="font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full text-xs">{pendingCounts.rateReconciliationCount}</span>
                      </Link>
                    )}
                    {canSeeVendorApprovals && (pendingCounts?.vendorApprovalCount || 0) > 0 && (
                      <Link to="/vendors?approval=Pending" onClick={() => setShowNotifDrop(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-3.5 h-3.5 text-orange-600" />
                          </div>
                          <span>Vendor Approvals</span>
                        </div>
                        <span className="font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full text-xs">{pendingCounts.vendorApprovalCount}</span>
                      </Link>
                    )}
                    {canSeeFinanceTasks && (pendingCounts?.postToFinanceCount || 0) > 0 && (
                      <Link to="/invoices?status=Approved&postingStatus=NotPosted" onClick={() => setShowNotifDrop(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                            <CheckCheck className="w-3.5 h-3.5 text-yellow-600" />
                          </div>
                          <span>Post to Finance</span>
                        </div>
                        <span className="font-semibold text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-full text-xs">{pendingCounts.postToFinanceCount}</span>
                      </Link>
                    )}
                    {canSeeAccruals && (pendingCounts?.unmatchedAccrualCount || 0) > 0 && (
                      <Link to="/accruals?status=Active" onClick={() => setShowNotifDrop(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                            <ClipboardList className="w-3.5 h-3.5 text-rose-600" />
                          </div>
                          <span>Unmatched Accruals</span>
                        </div>
                        <span className="font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full text-xs">{pendingCounts.unmatchedAccrualCount}</span>
                      </Link>
                    )}
                    {canSeePurchaseOrders && (pendingCounts?.unmatchedPOCount || 0) > 0 && (
                      <Link to="/purchase-orders?status=Unmatched" onClick={() => setShowNotifDrop(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                            <ClipboardList className="w-3.5 h-3.5 text-cyan-600" />
                          </div>
                          <span>Unmatched POs</span>
                        </div>
                        <span className="font-semibold text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded-full text-xs">{pendingCounts.unmatchedPOCount}</span>
                      </Link>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Inbox tab */}
            {notifTab === 'inbox' && (
              <>
                {inbox.length > 0 && (
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                    <span className="text-xs text-gray-400">{inboxUnread > 0 ? `${inboxUnread} unread` : 'All read'}</span>
                    {inboxUnread > 0 && (
                      <button
                        onClick={markAllRead}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        <CheckCheck className="w-3 h-3" />
                        Dismiss all
                      </button>
                    )}
                  </div>
                )}
                <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                  {inbox.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-400">No notifications</div>
                  ) : (
                    inbox.map(n => {
                      const { icon: NIcon } = notifMeta(n.NotificationType);
                      const path = getNotifPath(n);
                      return (
                        <div key={n.NotificationID}
                          className={`group flex items-start gap-3 px-4 py-3 transition-colors ${!n.IsRead ? 'bg-blue-50/40 hover:bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          {/* Clickable content area */}
                          <button
                            onClick={() => handleNotifClick(n)}
                            className="flex items-start gap-3 flex-1 min-w-0 text-left"
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${!n.IsRead ? 'bg-blue-100' : 'bg-gray-100'}`}>
                              <NIcon className={`w-4 h-4 ${!n.IsRead ? 'text-blue-600' : 'text-gray-400'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!n.IsRead ? 'font-semibold text-gray-900' : 'text-gray-600'}`}
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {n.Subject}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {formatNotifDate(n.CreatedAt)}
                                {path && <span className="ml-1.5 text-blue-400">→ View</span>}
                              </p>
                            </div>
                            {!n.IsRead && (
                              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                            )}
                          </button>
                          {/* Dismiss button */}
                          <button
                            onClick={e => { e.stopPropagation(); dismissNotification(n.NotificationID); }}
                            className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-gray-600 hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-all"
                            title="Dismiss"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Settings cog */}
      {SETTINGS_ITEMS.some(s => s.superAdminOnly ? user?.isSuperAdmin : hasPermission(s.permission)) && (
        <div className="relative" ref={settingsRef}>
          <button
            onClick={() => { setShowSettingsDrop(prev => !prev); setShowNotifDrop(false); setShowUserDrop(false); setShowHelpDrop(false); }}
            className={`relative p-2 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${showSettingsDrop ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : ''}`}
            title="Settings"
          >
            <Settings className="h-5 w-5" />
          </button>

          {showSettingsDrop && (
            <div className="absolute right-0 top-12 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Settings</p>
              </div>
              <div className="py-1 max-h-80 overflow-y-auto">
                {SETTINGS_ITEMS.filter(s => s.superAdminOnly ? user?.isSuperAdmin : hasPermission(s.permission)).map(item => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.path} to={item.path} onClick={() => setShowSettingsDrop(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help button */}
      <div className="relative" ref={helpRef}>
        <button
          onClick={() => { setShowHelpDrop(prev => !prev); setShowNotifDrop(false); setShowUserDrop(false); }}
          className="relative p-2 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title="Help & Support"
        >
          <HelpCircle className="h-5 w-5" />
        </button>

        {showHelpDrop && (
          <div className="absolute right-0 top-12 w-52 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Help & Support</p>
            </div>
            <div className="py-1">
              <Link to="/help" onClick={() => setShowHelpDrop(false)}
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                <BookOpen className="w-4 h-4 text-blue-500 flex-shrink-0" />
                Help Center
              </Link>
              <Link to="/support/new" onClick={() => setShowHelpDrop(false)}
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                <LifeBuoy className="w-4 h-4 text-green-500 flex-shrink-0" />
                Contact Support
              </Link>
              {hasPermission('manage_help') && (
                <>
                  <div className="mx-4 my-1 border-t border-gray-100" />
                  <Link to="/help/manage" onClick={() => setShowHelpDrop(false)}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                    <FilePlus className="w-4 h-4 text-purple-500 flex-shrink-0" />
                    Manage Articles
                  </Link>
                  <Link to="/support/tickets" onClick={() => setShowHelpDrop(false)}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                    <ClipboardList className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    Support Tickets
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Theme toggle – moved to profile dropdown */}

      {/* Voice AI Agent button — desktop only; mobile uses the FAB in Layout */}
      <div className="hidden lg:flex">
        <VoiceAgentButton />
      </div>

      {/* Chat button */}
      <button
        onClick={onOpenChat}
        className="relative p-2 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        title="Chat"
      >
        <MessageCircle className="h-5 w-5" />
        {unreadChat > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadChat > 9 ? '9+' : unreadChat}
          </span>
        )}
      </button>

      {/* Divider */}
      <div className="w-px h-8 bg-gray-200 dark:bg-gray-600" />

      {/* User profile */}
      <div className="relative" ref={userRef}>
        <button
          onClick={toggleUserDropdown}
          className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-semibold text-xs">{initials}</span>
          </div>
          <div className="text-left hidden xl:block">
            <p className="text-sm font-medium text-gray-900 dark:text-white leading-tight truncate max-w-[200px]">{displayName}</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight truncate max-w-[200px]">{roleBadge}</p>
          </div>
          <ChevronDown className={`h-3.5 w-3.5 text-gray-500 dark:text-gray-400 transition-transform ${showUserDrop ? 'rotate-180' : ''}`} />
        </button>

        {showUserDrop && (
          <div className="absolute right-0 top-14 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{initials}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{displayName}</p>
                  <p className="text-xs text-blue-100 truncate">{user?.email}</p>
                </div>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              <div className="px-4 py-2.5 flex items-center gap-3">
                <Shield className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Role</p>
                  <p className="text-sm text-gray-800 font-medium">{roleBadge}</p>
                </div>
              </div>
              <div className="px-4 py-2.5 flex items-center gap-3">
                <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Company</p>
                  <p className="text-sm text-gray-800 font-medium truncate">{companyName}</p>
                </div>
              </div>
              <div className="px-4 py-2.5 flex items-center gap-3">
                <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Session Time</p>
                  <p className="text-sm text-gray-800 font-medium">
                    {formatDuration(sessionSeconds)}
                    <span className="text-gray-400 text-xs ml-1">logged in</span>
                  </p>
                </div>
              </div>
              <div className="px-4 py-2.5 flex items-center gap-3">
                <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Login Type</p>
                  <p className="text-sm text-gray-800 font-medium capitalize">{user?.authType || 'Standard'}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 p-2 space-y-1">
              <Link
                to="/notification-preferences"
                onClick={() => setShowUserDrop(false)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Bell className="h-4 w-4" />
                Notification Settings
              </Link>
              <button
                onClick={() => { toggleTheme(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notification detail modal */}
      {selectedNotif && (
        <div
          className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
          onClick={() => setSelectedNotif(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {(() => {
              const { icon: NIcon, label } = notifMeta(selectedNotif.NotificationType);
              const path = getNotifPath(selectedNotif);
              return (
                <>
                  <div className="px-5 py-4 border-b border-gray-200 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <NIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
                      <h3 className="text-base font-semibold text-gray-900 mt-0.5">{selectedNotif.Subject}</h3>
                      <p className="text-xs text-gray-400 mt-1">{formatNotifDate(selectedNotif.CreatedAt)}</p>
                    </div>
                    <button
                      onClick={() => setSelectedNotif(null)}
                      className="text-gray-400 hover:text-gray-700 p-1"
                      title="Close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="px-5 py-4 text-sm text-gray-700 space-y-2">
                    {selectedNotif.EntityType && selectedNotif.EntityID && (
                      <p className="text-gray-500">
                        Linked to <span className="font-medium text-gray-700">{selectedNotif.EntityType}</span>
                      </p>
                    )}
                    {selectedNotif.Status && (
                      <p className="text-gray-500">Status: <span className="font-medium text-gray-700">{selectedNotif.Status}</span></p>
                    )}
                  </div>
                  <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-2">
                    <button
                      onClick={() => setSelectedNotif(null)}
                      className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                    {path && (
                      <button
                        onClick={handleNotifView}
                        className="px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        View
                      </button>
                    )}
                    <button
                      onClick={handleNotifAcknowledge}
                      className="px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                    >
                      Acknowledge
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
